/**
 * Token storage helpers for the webapp.
 *
 * - OTP-authenticated tokens (wa_otp / wa_otp_custom): stored in localStorage,
 *   scoped per tenant so multiple tenants on the same domain don't collide.
 *   Effectively never expire (JWT issued with 10-year TTL).
 *
 * - Magic-link tokens (from PingUp): stored in sessionStorage under the legacy
 *   key "access_token". Cleared when the tab closes (use-and-throw).
 */

const PERSISTENT_KEY = (tenantId) => `webapp_token_${tenantId}`;
const SESSION_KEY = "access_token";

/**
 * Save a long-lived OTP token to localStorage for a specific tenant.
 */
export function savePersistentToken(tenantId, token) {
  if (!tenantId || !token) return;
  localStorage.setItem(PERSISTENT_KEY(tenantId), token);
}

/**
 * Remove the persistent token for a tenant (logout).
 */
export function removePersistentToken(tenantId) {
  if (!tenantId) return;
  localStorage.removeItem(PERSISTENT_KEY(tenantId));
}

/**
 * Save a short-lived magic-link token to sessionStorage.
 */
export function saveSessionToken(token) {
  if (!token) return;
  sessionStorage.setItem(SESSION_KEY, token);
}

/**
 * Remove the session token (magic-link logout / expiry).
 */
export function removeSessionToken() {
  sessionStorage.removeItem(SESSION_KEY);
}

/**
 * Resolve the best available token for a given tenant.
 * Priority: sessionStorage (magic-link, fresh) > localStorage (OTP, persistent).
 */
export function resolveToken(tenantId) {
  const session = sessionStorage.getItem(SESSION_KEY);
  if (session) return session;
  if (tenantId) {
    const persistent = localStorage.getItem(PERSISTENT_KEY(tenantId));
    if (persistent) return persistent;
  }
  return null;
}

/**
 * Clear all tokens for a tenant (full logout).
 */
export function clearAllTokens(tenantId) {
  removeSessionToken();
  removePersistentToken(tenantId);
}
